"use client";

import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiArrowRightBold } from "react-icons/pi";
import logger from "@/utils/logger";

interface FeatureItem {
  title: string;
  description: string;
  icon: string;
}

export default function HokFeatures({
  content,
  onContentChange,
  isSelected,
}: SectionContentProps) {
  const primaryColor = content.colorPalette?.primary || "#684DF4";
  const secondaryColor = content.colorPalette?.secondary || "#8a2be2";
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  const defaultItems: FeatureItem[] = [
    {
      title: "User-Friendly Platforms",
      description: "All the Lorem Ipsum generators on the Internet tend to repeat predefined necessary.",
      icon: "/assets/img/icon/service_1_1.svg",
    },
    {
      title: "Seamless Experience",
      description: "All the Lorem Ipsum generators on the Internet tend to repeat predefined necessary.",
      icon: "/assets/img/icon/service_1_2.svg",
    },
    {
      title: "24/7 Support",
      description: "All the Lorem Ipsum generators on the Internet tend to repeat predefined necessary.",
      icon: "/assets/img/icon/service_1_3.svg",
    },
    {
      title: "Problem Solving",
      description: "All the Lorem Ipsum generators on the Internet tend to repeat predefined necessary.",
      icon: "/assets/img/icon/service_1_4.svg",
    },
    {
      title: "We are open source",
      description: "All the Lorem Ipsum generators on the Internet tend to repeat predefined necessary.",
      icon: "/assets/img/icon/service_1_5.svg",
    },
    {
      title: "Secure Transactions",
      description: "All the Lorem Ipsum generators on the Internet tend to repeat predefined necessary.",
      icon: "/assets/img/icon/service_1_6.svg",
    },
    {
      title: "Top Resources",
      description: "All the Lorem Ipsum generators on the Internet tend to repeat predefined necessary.",
      icon: "/assets/img/icon/service_1_7.svg",
    },
  ];

  const items = content.items || defaultItems;
  const subtitle = content.subtitle || "Our Features";
  const title = content.title || "Powerful AI Features";
  const ctaText = content.ctaText || "View All Features";
  const backgroundImage = content.backgroundImage || "/assets/img/bg/service_bg_1.png";

  const updateItem = (idx: number, field: keyof FeatureItem, value: string) => {
    if (!onContentChange) return;
    const newItems = [...items];
    newItems[idx] = { ...newItems[idx], [field]: value };
    onContentChange({ ...content, items: newItems });
  };

  // Group items into columns: Left (2 cards), Middle (3 cards), Right (2 cards)
  const leftColItems = [items[0], items[1]].filter(Boolean);
  const middleColItems = [items[2], items[3], items[4]].filter(Boolean);
  const rightColItems = [items[5], items[6]].filter(Boolean);

  // Soft pastel backgrounds for icons
  const getIconBgClass = (iconPath: string) => {
    if (iconPath.includes("service_1_1")) return "bg-blue-50";
    if (iconPath.includes("service_1_2")) return "bg-orange-50";
    if (iconPath.includes("service_1_3")) return "bg-pink-50";
    if (iconPath.includes("service_1_4")) return "bg-purple-50";
    if (iconPath.includes("service_1_5")) return "bg-emerald-50";
    if (iconPath.includes("service_1_6")) return "bg-teal-50";
    return "bg-indigo-50";
  };

  const renderCard = (item: FeatureItem, actualIdx: number) => {
    if (!item) return null;
    const isMiddleHighlighted = actualIdx === 3; // Index 3 is "Problem Solving"
    return (
      <div
        key={actualIdx}
        className={`bg-white rounded-[20px] px-6 py-12 flex flex-col items-center text-center transition-all duration-300 group w-full max-w-[290px] mx-auto min-h-[320px] justify-center ${isMiddleHighlighted
            ? "border-2 shadow-md hover:shadow-xl"
            : "border border-gray-100/70 shadow-sm hover:shadow-md hover:-translate-y-1"
          }`}
        style={isMiddleHighlighted ? { borderColor: primaryColor } : {}}
      >
        {/* Icon Container */}
        <div className={`relative w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 mb-6 transition-transform group-hover:scale-105 ${getIconBgClass(item.icon)}`}>
          <img src={item.icon} alt="icon" className="w-8 h-8 object-contain" />
          {isSelected && (
            <label className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 rounded-2xl cursor-pointer transition-all opacity-0 group-hover:opacity-100">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const toast = (await import("react-hot-toast")).default;
                    const homepageApi = (await import("@/services/homepageApi")).default;
                    const toastId = toast.loading("Uploading icon...");
                    try {
                      const response = await homepageApi.uploadImage(file);
                      const url = response.data?.url || response.url;
                      if (url) {
                        updateItem(actualIdx, "icon", url);
                        toast.success("Icon updated!", { id: toastId });
                      } else {
                        toast.error("Failed to upload.", { id: toastId });
                      }
                    } catch (err) {
                      toast.error("Upload failed", { id: toastId });
                      logger.error(err);
                    }
                  }
                }}
              />
            </label>
          )}
        </div>

        {/* Content */}
        <div className="w-full flex flex-col items-center">
          <h3 className="text-base font-bold text-gray-900 mb-3 leading-snug">
            <EditableText
              value={item.title}
              onChange={(val) => updateItem(actualIdx, "title", val)}
              as="span"
              placeholder="Feature Title"
            />
          </h3>
          <p className="text-sm text-gray-500 leading-relaxed max-w-[260px]">
            <EditableText
              value={item.description}
              onChange={(val) => updateItem(actualIdx, "description", val)}
              as="span"
              placeholder="Feature Description"
            />
          </p>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`relative overflow-hidden w-full py-10 sm:py-14 bg-[#FAF9FF] ${isSelected ? "ring-2 ring-blue-500 relative z-20 shadow-md" : ""
        }`}
      style={{
        fontFamily,
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span
            className="inline-block px-4 py-1.5 text-xs sm:text-sm font-bold tracking-wider rounded-full mb-4 uppercase border"
            style={{
              color: primaryColor,
              backgroundColor: `${primaryColor}10`,
              borderColor: `${primaryColor}20`,
            }}
          >
            <EditableText
              value={subtitle}
              onChange={(val) =>
                onContentChange && onContentChange({ ...content, subtitle: val })
              }
              as="span"
              placeholder="Subtitle"
            />
          </span>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight leading-tight">
            <EditableText
              value={title}
              onChange={(val) =>
                onContentChange && onContentChange({ ...content, title: val })
              }
              as="span"
              placeholder="Powerful AI Features"
            />
          </h2>
        </div>

        {/* 3-Column Staggered Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 items-center max-w-[890px] mx-auto">
          {/* Column 1 (Left): 2 Cards */}
          <div className="flex flex-col gap-2">
            {leftColItems.map((item, idx) => renderCard(item, items.indexOf(item)))}
          </div>

          {/* Column 2 (Middle): 3 Cards */}
          <div className="flex flex-col gap-2">
            {middleColItems.map((item, idx) => renderCard(item, items.indexOf(item)))}
          </div>

          {/* Column 3 (Right): 2 Cards */}
          <div className="flex flex-col gap-2">
            {rightColItems.map((item, idx) => renderCard(item, items.indexOf(item)))}
          </div>
        </div>

        {/* CTA Button */}
        {content.showCta !== false && (
          <div className="flex justify-center mt-12 sm:mt-16">
            <a
              href={content.ctaUrl || "#"}
              onClick={(e) => {
                if (onContentChange) {
                  e.preventDefault();
                }
              }}
              className="rounded px-8 py-3.5 text-sm font-bold text-white transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2 group"
              style={{
                background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
              }}
            >
              <EditableText
                value={ctaText}
                onChange={(val) =>
                  onContentChange && onContentChange({ ...content, ctaText: val })
                }
                as="span"
                placeholder="View All Features"
              />
              <PiArrowRightBold className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
